import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { getMonthNotes, MonthNote, putMonthNotes } from "../../api/archive";
import { extractErrorMessage } from "../../api/client";

// Per-month errata editor. Notes are the human layer of the archive
// description (e.g. "CNN capture outage 2024-03-04 → 06") — stored
// separately from the machine-computed stats, so re-describing a month
// never clobbers them. Loads on open, PUTs the full list on save.

interface Props {
  open: boolean;
  year: string;
  month: string;
  authorEmail: string | null;
  onClose: (saved: boolean) => void;
}

export function EditNotesDialog({ open, year, month, authorEmail, onClose }: Props) {
  const [notes, setNotes] = useState<MonthNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setLoading(true);
    getMonthNotes(year, month)
      .then((r) => setNotes(r.notes))
      .catch((err) => setError(extractErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [open, year, month]);

  const updateText = (i: number, text: string) =>
    setNotes((prev) => prev.map((n, idx) => (idx === i ? { ...n, text } : n)));
  const remove = (i: number) => setNotes((prev) => prev.filter((_, idx) => idx !== i));
  const add = () =>
    setNotes((prev) => [
      ...prev,
      { text: "", author: authorEmail ?? undefined, created_at: new Date().toISOString() },
    ]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      // Drop blank notes so an "Add note" the user didn't fill in
      // doesn't persist an empty entry.
      const cleaned = notes
        .map((n) => ({ ...n, text: n.text.trim() }))
        .filter((n) => n.text.length > 0);
      await putMonthNotes(year, month, cleaned);
      onClose(true);
    } catch (err) {
      setError(extractErrorMessage(err));
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} fullWidth maxWidth="sm">
      <DialogTitle>
        Errata — {year}-{month}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Per-month notes (capture outages, quality caveats, corrections). Merged into the
          archive description; the describe job never overwrites them.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {loading ? (
          <CircularProgress size={20} />
        ) : (
          <Stack spacing={1.5}>
            {notes.map((n, i) => (
              <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  fullWidth
                  multiline
                  size="small"
                  value={n.text}
                  onChange={(e) => updateText(i, e.target.value)}
                  placeholder="e.g. CNN capture outage 2024-03-04 → 06"
                  helperText={n.author ? `— ${n.author}` : undefined}
                />
                <IconButton aria-label="remove note" onClick={() => remove(i)} size="small">
                  <Delete fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            {notes.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No notes yet.
              </Typography>
            )}
            <Button
              startIcon={<Add />}
              onClick={add}
              size="small"
              sx={{ alignSelf: "flex-start" }}
            >
              Add note
            </Button>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={loading || saving}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
